import { checkCompose, MAX_SEGMENTS, maxCharacters } from '@fundline/shared/sms/compose';
import { toGsmSafe } from '@fundline/shared/sms/segments';
import { ArrowUp } from 'lucide-react';
import {
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  useId,
  useMemo,
  useState,
} from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { TOUCH_QUERY, useMediaQuery } from '../../lib/use-media-query.ts';

export interface ComposeBoxProps {
  /** The merchant's first name (or business) for the placeholder, "Text Mike…". */
  recipientName: string | null;
  /** The opt-out footer the database will add (first text to this merchant), or null. */
  footer: string | null;
  /** Why this merchant cannot be texted, or null. */
  disabledReason: string | null;
  /** The quiet-hours notice (M7): when this text would go out, if not now. */
  notice?: ReactNode;
  sending: boolean;
  error: string | null;
  /** Sends; resolves true when the database accepted it (the draft is then cleared). */
  onSend: (input: { body: string; finalText: string; clientKey: string }) => Promise<boolean>;
}

/**
 * The compose box (CLAUDE.md §1.4): live segment count with the real encoding (footer included), a
 * warning naming the characters that force Unicode, a "Replace look-alikes" review that shows every
 * change before applying it, the 10-segment cap, and an explicit confirmation for more than one segment.
 * The count is always shown (CLAUDE.md wins over the brand kit's "after 160 characters"). Enter sends on
 * desktop and Shift+Enter adds a line; on touch devices Enter adds a line and the send button sends.
 */
export function ComposeBox({
  recipientName,
  footer,
  disabledReason,
  notice = null,
  sending,
  error,
  onSend,
}: ComposeBoxProps) {
  const touch = useMediaQuery(TOUCH_QUERY);
  const [draft, setDraft] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  // One key per send attempt of one draft: a double tap or a retry after a network error reuses it,
  // so the database stores the text once.
  const [attempt, setAttempt] = useState<{ text: string; key: string } | null>(null);
  const id = useId();

  const body = draft.trim();
  const check = useMemo(() => checkCompose(body, footer), [body, footer]);
  const { info } = check;
  const unicode = info.encoding === 'ucs2';
  const canSend =
    !disabledReason && body.length > 0 && !check.overCap && (!check.needsConfirmation || confirmed);

  function change(next: string) {
    setDraft(next);
    setConfirmed(false);
    setReviewing(false);
  }

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!canSend || sending) return;
    const key = attempt?.text === check.finalText ? attempt.key : crypto.randomUUID();
    setAttempt({ text: check.finalText, key });
    const ok = await onSend({ body, finalText: check.finalText, clientKey: key });
    if (ok) {
      setDraft('');
      setConfirmed(false);
      setAttempt(null);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
    // Touch keyboards: return adds a line, unless a hardware keyboard sends Ctrl/Cmd+Enter.
    if (touch && !e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    void submit();
  }

  if (disabledReason) {
    return (
      <div
        className="callout m-3 rounded-md bg-danger-tint px-4 py-3 text-ink"
        data-testid="compose-disabled"
      >
        Texting is off for this merchant: {disabledReason}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="flex flex-col gap-2 px-4 py-2"
      aria-label="Compose a text"
    >
      {notice}
      <label htmlFor={id} className="sr-only">
        Message
      </label>
      <div className="fl-composer__field focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-focus-ring">
        <textarea
          id={id}
          value={draft}
          onChange={(e) => change(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          enterKeyHint={touch ? 'enter' : 'send'}
          placeholder={recipientName ? `Text ${recipientName}…` : 'Write a text…'}
          className="fl-composer__input field-sizing-content max-h-28 min-h-7 overflow-y-auto"
        />
        <button
          type="submit"
          className="fl-send mb-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:cursor-not-allowed"
          disabled={!canSend || sending}
          aria-busy={sending || undefined}
          aria-label={info.segments > 1 ? `Send ${info.segments} segments` : 'Send'}
          title={touch ? 'Send' : 'Send (Enter)'}
        >
          {sending ? (
            <span
              className="size-4 animate-spin rounded-pill border-2 border-current border-t-transparent"
              aria-hidden
            />
          ) : (
            <ArrowUp size={20} strokeWidth={2} aria-hidden />
          )}
        </button>
      </div>

      {footer && body.length > 0 && check.finalText !== body ? (
        <p className="fl-meta" data-testid="compose-footer">
          First text to this merchant: “{footer}” is added on its own line and counted below.
        </p>
      ) : null}

      <div className="fl-meta flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span
          data-testid="segment-count"
          className={
            check.overCap
              ? 'font-semibold text-danger'
              : info.segments > 1
                ? 'font-semibold text-warning'
                : ''
          }
        >
          {info.segments} {info.segments === 1 ? 'segment' : 'segments'} of {MAX_SEGMENTS} max ·{' '}
          {unicode ? 'Unicode' : 'GSM-7'} · {info.characters} characters
          {info.segments > 0 ? ` · ${info.remaining} left in this segment` : ''}
        </span>
        <span data-testid="segment-cap">
          Limit: {maxCharacters(info.encoding).toLocaleString()} characters with{' '}
          {unicode ? 'Unicode' : 'GSM-7'}
        </span>
      </div>

      {unicode ? (
        <div role="alert" className="callout rounded-md bg-warning-tint p-3 text-ink">
          <p>
            These characters switch the text to Unicode, which cuts each segment from 160 to 70
            characters and can double or triple the cost:{' '}
            <span className="fl-num">{check.costlyCharacters.join(' ')}</span>
          </p>
          {check.lookAlikes.length > 0 && !reviewing ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={() => setReviewing(true)}
            >
              Replace look-alikes…
            </Button>
          ) : null}
          {reviewing ? (
            <div
              className="mt-2 rounded-md bg-surface-raised p-3 text-ink ring-1 ring-hairline"
              data-testid="lookalike-review"
            >
              <p className="font-semibold">These characters will change:</p>
              <ul className="mt-1 flex flex-col gap-0.5">
                {check.lookAlikes.map((c) => (
                  <li key={c.from} className="callout">
                    <span className="fl-num">
                      “{c.from}” → “{c.to === '' ? '(removed)' : c.to}”
                    </span>{' '}
                    <span className="footnote text-ink-muted">
                      {c.name}
                      {c.count > 1 ? `, ${c.count} times` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="footnote mt-1 text-ink-muted">
                {check.replacementSavesSegments
                  ? `Segments: ${info.segments} → ${check.segmentsAfterReplacement}.`
                  : check.costlyCharacters.some(
                        (ch) => !check.lookAlikes.some((l) => l.from === ch),
                      )
                    ? 'Other characters (like emoji) keep the text in Unicode; remove them too to save segments.'
                    : 'The segment count stays the same.'}
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    change(toGsmSafe(draft).text);
                  }}
                >
                  Replace
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setReviewing(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {check.overCap ? (
        <Alert tone="error">
          Too long: {info.segments} segments. A text can have at most {MAX_SEGMENTS} segments (
          {maxCharacters(info.encoding).toLocaleString()} characters with{' '}
          {unicode ? 'Unicode' : 'GSM-7'}).
        </Alert>
      ) : null}

      {check.needsConfirmation && !check.overCap ? (
        <label className="callout flex items-start gap-2 text-ink">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 size-5 accent-brand"
          />
          <span>
            Send as {info.segments} segments. It is billed as {info.segments} texts.
          </span>
        </label>
      ) : null}

      {error ? <Alert tone="error">{error}</Alert> : null}

      {touch ? null : <p className="fl-meta">Enter to send · Shift+Enter for a new line</p>}
    </form>
  );
}
